import { useMemo, useState } from 'react';

import { NavLink as RouterLink, useSearchParams } from 'react-router-dom';

import {
  Center,
  Flex,
  Link,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';

import { format, formatDistanceToNowStrict, isValid, parseISO } from 'date-fns';

import {
  Badge,
  DropdownInput,
  DropdownOption,
  IntegrationSyncRun,
  IntegrationSyncStatus,
  IntegrationType,
} from '@aslanpay/portal-common-lib';

import AuthenticatedPageWrapper from 'app/components/UI/AuthenticatedPageWrapper';

import useFetchCompanies from 'app/hooks/useFetchCompanies';
import useFetchIntegrationSyncRuns from 'app/hooks/useFetchIntegrationSyncRuns';
import useFetchLatestIntegrationSyncRuns from 'app/hooks/useFetchLatestIntegrationSyncRuns';

import { getRunSyncStatus, SYNC_STATUS_DETAILS } from 'app/utils/integrationSync';
import { getSelectedCompanyPath } from 'app/utils/routes';

import IntegrationSyncRunDetailDrawer from './IntegrationSyncRunDetailDrawer';

const ROW_TINTS: Record<IntegrationSyncStatus, string | undefined> = {
  SUCCESS: undefined,
  PARTIAL_SUCCESS: 'orange.50',
  FAILED: 'red.50',
};

const SYNC_TYPE_OPTIONS: IntegrationType[] = ['HRIS_SYNC', 'TIMESHEET_SYNC', 'PAYROLL_OUTPUT'];

const SYNC_TYPE_LABELS: Record<IntegrationType, string> = {
  HRIS_SYNC: 'HRIS Sync',
  TIMESHEET_SYNC: 'Timesheet Sync',
  PAYROLL_OUTPUT: 'Payroll Output',
};

const SYNC_TYPE_DROPDOWN_OPTIONS: DropdownOption[] = [
  { value: '', text: 'All sync types' },
  ...SYNC_TYPE_OPTIONS.map((type) => ({ value: type, text: SYNC_TYPE_LABELS[type] })),
];

const formatRelativeDate = (value: string) => {
  const date = parseISO(value);
  return isValid(date) ? formatDistanceToNowStrict(date, { addSuffix: true }) : '—';
};

const formatAbsoluteDate = (value: string) => {
  const date = parseISO(value);
  return isValid(date) ? format(date, 'dd MMM yyyy, HH:mm') : '—';
};

const isSyncType = (value: string | null): value is IntegrationType =>
  value !== null && (SYNC_TYPE_OPTIONS as string[]).includes(value);

const sortByMostRecent = (runs: IntegrationSyncRun[]) =>
  [...runs].sort((a, b) => new Date(b.syncedAt).getTime() - new Date(a.syncedAt).getTime());

function RunsTable({
  runs,
  isLoading,
  isError,
  emptyMessage,
  companyNameById,
  onSelectRun,
}: {
  runs: IntegrationSyncRun[] | undefined;
  isLoading: boolean;
  isError: boolean;
  emptyMessage: string;
  companyNameById: Map<string, string>;
  onSelectRun: (syncRunId: string) => void;
}) {
  if (isLoading) {
    return (
      <Center py="40px">
        <Spinner color="brand.purple" size="md" />
      </Center>
    );
  }

  if (isError) {
    return (
      <Center py="40px">
        <Text textStyle="smallRegular" color="red.500">
          Failed to load sync runs. Please try again.
        </Text>
      </Center>
    );
  }

  if (!runs || runs.length === 0) {
    return (
      <Center py="40px">
        <Text textStyle="smallRegular" color="brand.darkGray">
          {emptyMessage}
        </Text>
      </Center>
    );
  }

  return (
    <Flex overflowX="auto" border="1px solid" borderColor="brand.lightGray1" borderRadius="8px">
      <Table variant="simple" size="sm">
        <Thead bgColor="brand.lightGray2">
          <Tr>
            <Th>Status</Th>
            <Th>Provider</Th>
            <Th>Type</Th>
            <Th>Company</Th>
            <Th>Synced</Th>
            <Th>Error</Th>
          </Tr>
        </Thead>
        <Tbody>
          {sortByMostRecent(runs).map((run) => (
            <Tr
              key={run.syncRunId}
              role="button"
              tabIndex={0}
              cursor="pointer"
              _hover={{ bgColor: 'brand.lightGray2' }}
              onClick={() => onSelectRun(run.syncRunId)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
                  event.preventDefault();
                  onSelectRun(run.syncRunId);
                }
              }}
              bgColor={ROW_TINTS[getRunSyncStatus(run)]}
            >
              <Td>
                <Badge
                  value={SYNC_STATUS_DETAILS[getRunSyncStatus(run)].label}
                  status={SYNC_STATUS_DETAILS[getRunSyncStatus(run)].badgeStatus}
                />
              </Td>
              <Td fontSize="sm">{run.provider}</Td>
              <Td fontSize="sm">{SYNC_TYPE_LABELS[run.syncType] ?? run.syncType}</Td>
              <Td fontSize="sm">
                <Link
                  as={RouterLink}
                  to={getSelectedCompanyPath(run.companyId)}
                  color="brand.purple"
                  textDecoration="underline"
                  onClick={(event) => event.stopPropagation()}
                >
                  {companyNameById.get(run.companyId) ?? run.companyId}
                </Link>
              </Td>
              <Td fontSize="sm">
                <Text>{formatRelativeDate(run.syncedAt)}</Text>
                <Text textStyle="extraSmallRegular" color="brand.darkGray">
                  {formatAbsoluteDate(run.syncedAt)}
                </Text>
              </Td>
              <Td fontSize="xs" color="red.500" maxW="240px" isTruncated>
                {run.error}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Flex>
  );
}

function IntegrationSyncHistory() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedSyncRunId, setSelectedSyncRunId] = useState<string | undefined>();

  const companyId = searchParams.get('companyId') ?? undefined;
  const syncTypeParam = searchParams.get('syncType');
  const syncType = isSyncType(syncTypeParam) ? syncTypeParam : undefined;
  const isFiltered = !!companyId || !!syncType;

  const updateParam = (key: string, value: string | undefined) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    setSearchParams(params, { replace: true });
  };

  const { data: companiesData, isInitialLoading: isLoadingCompanies } = useFetchCompanies({
    page: 0,
    size: 999999,
    sortBy: 'name',
    sort: 'ASC',
  });
  const companies = useMemo(() => companiesData?.list ?? [], [companiesData]);

  const companyOptions: DropdownOption[] = useMemo(
    () => [
      { value: '', text: 'All companies' },
      ...companies.map((company) => ({ value: company.id, text: company.name })),
    ],
    [companies]
  );
  const companyNameById = useMemo(
    () => new Map(companies.map((company) => [company.id, company.name])),
    [companies]
  );

  const {
    data: latestRuns,
    isLoading: isLoadingLatest,
    isError: isLatestError,
  } = useFetchLatestIntegrationSyncRuns(!isFiltered);
  const {
    data: filteredRuns,
    isLoading: isLoadingFiltered,
    isError: isFilteredError,
  } = useFetchIntegrationSyncRuns({ companyId, syncType, limit: 100 }, isFiltered);

  const failingLatestRuns = useMemo(
    () => (latestRuns ?? []).filter((run) => getRunSyncStatus(run) !== 'SUCCESS'),
    [latestRuns]
  );
  const succeedingLatestRuns = useMemo(
    () => (latestRuns ?? []).filter((run) => getRunSyncStatus(run) === 'SUCCESS'),
    [latestRuns]
  );

  return (
    <AuthenticatedPageWrapper title="Sync history">
      <Flex flexDir="column" w="full" gap="16px">
        <Text as="h2" textStyle="h2" color="brand.darkPurple">
          Sync history
        </Text>
        <Text textStyle="extraSmallRegular" color="brand.darkGray">
          Every HRIS, timesheet and payroll-output sync across every company. Filter by company or
          type below, or clear the filters to see the latest outcome per company - failures surfaced
          first.
        </Text>

        <Flex gap="12px" align="center">
          <DropdownInput
            placeholder="All sync types"
            options={SYNC_TYPE_DROPDOWN_OPTIONS}
            defaultValue={SYNC_TYPE_DROPDOWN_OPTIONS.find(
              (option) => option.value === (syncType ?? '')
            )}
            onChange={(value) => updateParam('syncType', value ? String(value) : undefined)}
            style={{ width: '220px' }}
          />
          <DropdownInput
            placeholder="All companies"
            isDisabled={isLoadingCompanies}
            isSearchable
            searchPlaceholder="Search companies..."
            options={companyOptions}
            defaultValue={companyOptions.find((option) => option.value === (companyId ?? ''))}
            onChange={(value) => updateParam('companyId', value ? String(value) : undefined)}
            style={{ width: '320px' }}
          />
        </Flex>

        {isFiltered ? (
          <RunsTable
            runs={filteredRuns}
            isLoading={isLoadingFiltered}
            isError={isFilteredError}
            emptyMessage="No sync runs match these filters."
            companyNameById={companyNameById}
            onSelectRun={setSelectedSyncRunId}
          />
        ) : (
          <Flex flexDir="column" gap="24px">
            <Flex flexDir="column" gap="8px">
              <Text textStyle="smallSemiBold" color="brand.darkPurple">
                Needs attention ({failingLatestRuns.length})
              </Text>
              <RunsTable
                runs={failingLatestRuns}
                isLoading={isLoadingLatest}
                isError={isLatestError}
                emptyMessage="No company's most recent sync is currently failing or partial."
                companyNameById={companyNameById}
                onSelectRun={setSelectedSyncRunId}
              />
            </Flex>
            <Flex flexDir="column" gap="8px">
              <Text textStyle="smallSemiBold" color="brand.darkPurple">
                Latest successful syncs ({succeedingLatestRuns.length})
              </Text>
              <RunsTable
                runs={succeedingLatestRuns}
                isLoading={isLoadingLatest}
                isError={isLatestError}
                emptyMessage="No successful syncs recorded yet."
                companyNameById={companyNameById}
                onSelectRun={setSelectedSyncRunId}
              />
            </Flex>
          </Flex>
        )}
      </Flex>

      <IntegrationSyncRunDetailDrawer
        syncRunId={selectedSyncRunId}
        onClose={() => setSelectedSyncRunId(undefined)}
      />
    </AuthenticatedPageWrapper>
  );
}

export default IntegrationSyncHistory;
